{
  "schemaVersion": 1,
  "id": "staggered-cantilever-joints",
  "version": "1.0",
  "name": "Staggered cantilever: continuous divider housed into the shelves, dowelled end panels and posts, screwed back blocks",
  "family": "staggered-cantilever",
  "familyId": "staggered-cantilever",
  "jointFamily": "mixed",
  "provisional": true,
  "reviewer": null,
  "reviewedAt": null,
  "selfAuthored": true,
  "author": "David Mansaray",
  "authoredAt": "2026-10-09",
  "sources": [
    "research/taxonomy.md §1.4 Staggered cantilever: boards and joints in solid timber, construction notes (cantilever about 35 % of the supported span at 25 mm, every horizontal on two supports, merged end panels, one-board divider, anti-tip)",
    "PLAN.md §5: housed dado one third of the thickness deep; dowel butt two or three 8 mm dowels per 300 mm; screwed butt with countersunk heads; glue-ups from boards up to maxWidth",
    "docs/reviews/fable-spike/ (David's sheesham S-shelf: the fitted parameter block and the merge rule)"
  ],
  "joint": "One continuous divider runs from the bottom to the top and crosses every shelf in a housing (the shelf is notched from the back, the divider housed one third of the thickness either side). End panels and posts are dowelled between the horizontals above and below; end panels that line up across tiers with no shelf between them are one board. Back blocks sit between the horizontals at the back, screwed through the horizontals and into the divider.",
  "joints": [
    { "at": "divider-shelf", "kind": "housed-dado", "params": { "depthRatio": 0.3333 }, "note": "Through-housing at every crossing: the divider is one board; shelves are notched to half depth from the back." },
    { "at": "divider-bottom", "kind": "dowel-butt", "params": { "dowelsPer300": 3 }, "note": "Divider ends dowelled into the bottom and the top." },
    { "at": "end-shelf", "kind": "dowel-butt", "params": { "dowelsPer300": 3 }, "note": "End panels dowelled into the horizontal above and below; glue, clamp square." },
    { "at": "post-shelf", "kind": "dowel-butt", "params": { "dowelsPer300": 2 }, "note": "Two 8 mm dowels per post end." },
    { "at": "block-shelf", "kind": "screwed-butt", "params": { "screwSpec": "4.0 × 50 mm countersunk" }, "note": "Back blocks screwed through the horizontals and into the divider from behind; heads plugged where visible." }
  ],
  "envelope": {
    "W": { "min": 4000, "max": 16000 },
    "H": { "min": 5000, "max": 21000 },
    "D": { "min": 2000, "max": 4000 },
    "n": { "min": 2, "max": 8 },
    "minOpeningTicks": 1500
  },
  "limits": { "maxSpanMm": 700, "maxCantileverRatio": 0.35, "minOpeningMm": 150 },
  "edgeAllowanceTicks": 20,
  "cutAllowanceTicks": { "l": 0, "w": 0 },
  "arrisMm": 1,
  "intendedUse": "Domestic books and objects; shelves loaded evenly, heaviest items over the supports. Not for climbing, not structural.",
  "loadStatement": "Self-authored: no rated load. Keep heavy items over the divider, end panels and back blocks, not on the free cantilevered ends.",
  "edgeTreatment": "Solid timber: planed edges, 1 mm arris, free cantilevered ends show end grain, oiled with the faces. Plywood: exposed ply edges, sanded arris.",
  "fasteners": [
    { "id": "dowel", "spec": "8 × 40 mm fluted beech dowel", "qtyRule": "per-joint", "perJoint": 3, "use": "Three dowels per dowelled joint (two per post end), glued; drill 8 mm, 21 mm deep each side with a dowelling jig." },
    { "id": "block-screw", "spec": "4.0 × 50 mm countersunk wood screw", "qtyRule": "back-perimeter-and-shelves", "centresMm": 150, "use": "Through each horizontal into the back blocks and from the block into the divider at 150 mm centres, glued." },
    { "id": "wall-strap", "spec": "Anti-tip strap with fixings suited to the wall", "qtyRule": "fixed", "qty": 2, "use": "Two straps from the top and the highest end panel into studs, or with plugs or anchors suited to the wall." }
  ],
  "backFixing": {
    "method": "No full back. Back blocks are screwed between the horizontals and to the divider; they and the dowelled end panels resist racking together with the wall fixing.",
    "spacingMm": 150,
    "rackingNote": "Open-backed: the housings, back blocks and wall straps are the racking elements. Square the stack (check diagonals) as each tier is glued."
  },
  "wallFixing": {
    "required": true,
    "method": "Two anti-tip straps from the top into studs, or with fixings suited to the wall (masonry plugs or cavity anchors).",
    "hardware": "2 × anti-tip strap, screws and plugs to suit the wall"
  },
  "antiTip": {
    "required": true,
    "method": "Required above 1000 mm tall: two anti-tip straps before loading. Load the lowest tiers first and keep weight off the free ends."
  },
  "warnings": { "maxShelfSpanMm": 700, "antiTipAboveMm": 1000 },
  "assemblySteps": [
    "Glue up every panel listed as a glue-up first; flatten and cut to finished size.",
    "Cut the divider as one board; mark every shelf crossing from the bottom end and cut the housings and the matching shelf notches.",
    "Sand faces to 180 grit and break every arris; seal the end grain of the free ends.",
    "Drill the dowel holes in the end panels, posts and horizontals with a jig, from the datums in the cut list.",
    "Dry-fit tier by tier from the bottom: bottom, divider, then each shelf over the divider with its end panels and posts.",
    "Glue and clamp, checking each tier for square; screw the back blocks between the horizontals and into the divider.",
    "Finish (two coats, de-nib between).",
    "Install: stand it level, fit the anti-tip straps before loading."
  ],
  "markingDrilling": [
    "Mark every shelf datum (underside from the floor) on the divider from the bottom end; the housings sit on these lines.",
    "Dowel centres 40 mm from the front and back edges and centred on the board thickness."
  ],
  "installation": [
    "Stand the shelf in place, level it and pack under the end panels if the floor is uneven.",
    "Fix two anti-tip straps from the top into the wall before loading any shelf.",
    "Load the lowest tiers first; keep heavy items off the free ends."
  ],
  "tolerances": { "panelLength": 0.5, "panelWidth": 0.5, "squareness": "≤ 1 mm over each tier's diagonal", "shelfDatum": 1 }
}
